import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CreditDTO, fetchCreditById, fetchCreditsByUser } from '../../services/creditService';
import '../../styles/AccountTools.css';

const formatCreditAmount = (credit: CreditDTO) => {
  const amount = Number(credit.balance ?? credit.amount ?? 0);
  if (credit.unit === 'TIME') return `${amount.toFixed(0)} min`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: credit.currency || 'USD',
  }).format(amount);
};

const Credits = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [credits, setCredits] = useState<CreditDTO[]>([]);
  const [selectedCredit, setSelectedCredit] = useState<CreditDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadCredits = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const page = await fetchCreditsByUser(userId);
      setCredits(page.content || []);
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to load credits.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  const totals = useMemo(() => {
    return credits.reduce(
      (acc, credit) => {
        const balance = Number(credit.balance ?? credit.amount ?? 0);
        if (credit.unit === 'TIME') acc.time += balance;
        else acc.money += balance;
        return acc;
      },
      { money: 0, time: 0 }
    );
  }, [credits]);

  const openCredit = async (creditId: string) => {
    try {
      setSelectedCredit(await fetchCreditById(creditId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to load credit.');
    }
  };

  if (!userId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to view and apply for credits.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <p className="tool-breadcrumb"><Link to="/account/payments">Payments &amp; Credits</Link> / Credits</p>
          <h1>Credits</h1>
          <p>Rewards and available credits.</p>
        </div>
        <div className="tool-actions">
          <Link className="tool-btn-secondary" to="/account/credits/applications">Applications</Link>
          <Link className="tool-btn" to="/account/credits/apply">Apply for Credit</Link>
        </div>
      </div>

      {message && <div className="tool-message error">{message}</div>}

      <div className="tool-grid">
        <section className="tool-card">
          <h3>Money Credit Balance</h3>
          <p className="summary-amount">
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.money)}
          </p>
        </section>
        <section className="tool-card">
          <h3>Time Credit Balance</h3>
          <p className="summary-amount">{totals.time.toFixed(0)} min</p>
        </section>
      </div>

      <section className="tool-card" style={{ marginTop: 18 }}>
        <h2>Your Credits</h2>
        {loading ? (
          <div className="tool-loading">Loading credits...</div>
        ) : credits.length === 0 ? (
          <div className="tool-empty">No credits found.</div>
        ) : (
          <div className="tool-list">
            {credits.map((credit) => (
              <button className="tool-row" type="button" key={credit.id} onClick={() => openCredit(credit.id)}>
                <div className="tool-row-main">
                  <div className="tool-row-title">{credit.code ? `#${credit.code}` : 'Credit'}</div>
                  <div className="tool-row-sub">
                    {credit.status || 'Status unavailable'} - Expires {credit.expiresAt ? new Date(credit.expiresAt).toLocaleDateString() : 'not set'}
                  </div>
                </div>
                <span className="tool-pill">{formatCreditAmount(credit)}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {selectedCredit && (
        <div className="tool-modal" role="dialog" aria-modal="true">
          <div className="tool-modal-card">
            <div className="tool-modal-head">
              <h3>{selectedCredit.code ? `Credit #${selectedCredit.code}` : 'Credit Details'}</h3>
              <button className="tool-close" type="button" onClick={() => setSelectedCredit(null)}>x</button>
            </div>
            <div className="tool-list">
              <Info label="Amount" value={formatCreditAmount(selectedCredit)} />
              <Info label="Status" value={selectedCredit.status || 'Not available'} />
              <Info label="Issued" value={selectedCredit.issuedAt ? new Date(selectedCredit.issuedAt).toLocaleString() : 'Not available'} />
              <Info label="Expires" value={selectedCredit.expiresAt ? new Date(selectedCredit.expiresAt).toLocaleString() : 'Not available'} />
              <Info label="Note" value={selectedCredit.note || 'Not provided'} />
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="tool-row">
      <div className="tool-row-title">{label}</div>
      <div className="tool-row-sub">{value}</div>
    </div>
  );
}

export default Credits;
